<script setup lang="ts">
import { setNavActiveLink } from '@/utils/navActiveLink'
import { ApiError } from '@/api/client'
import { getOffering } from '@/api/offerings'
import OfferingWizard from '@/components/OfferingWizard.vue'
import type { Offering } from '@/types'

definePage({
  meta: { requiresAuth: true, centerConfig: true },

  /*
   * Qué item del menú queda marcado. El wizard no tiene item propio: sin esto el
   * grupo Offerings se cierra al entrar, porque ninguno de sus hijos matchea.
   *
   * El tipo real sale de la offering, que todavía no se cargó, así que se toma
   * del `kind` que manda el listado del que se entró. Es solo una pista para el
   * menú —el `setup` la corrige con el dato verdadero en cuanto responde la API,
   * y por eso entrar con la URL a mano tampoco puede quedar mal—, pero evita que
   * se encienda el item equivocado durante toda la carga.
   *
   * Va en el guard y no en el `setup` porque el grupo decide si está abierto una
   * sola vez por navegación. Inline y no por `utils/navActiveLink.ts` porque
   * `definePage` extrae este bloque fuera del módulo: acá adentro los imports del
   * `script setup` no existen.
   */
  beforeEnter: to => {
    const target = to.query.kind === 'product'
      ? 'dive-centers-id-offerings-rentals'
      : 'dive-centers-id-offerings-services'

    to.meta.navActiveLink = target

    const leaf = to.matched[to.matched.length - 1]

    if (leaf)
      leaf.meta.navActiveLink = target

    return true
  },
})

const route = useRoute('dive-centers-id-offerings-offering-id')
const id = String(route.params.id)
const offeringId = String(route.params.offeringId)

// `?documents=1` viene de "Attach documents" del listado: abre el wizard
// directamente en el último paso en vez de hacer recorrerlo.
const startAtDocuments = computed(() => route.query.documents === '1')

const offering = ref<Offering | null>(null)
const loading = ref(true)
const loadError = ref('')

onMounted(async () => {
  try {
    const res = await getOffering(id, offeringId)

    offering.value = res.offering

    /*
     * El item del menú, ya con el tipo verdadero. Se asigna en las DOS ramas y
     * no solo para los alquileres: el valor queda escrito en el registro de la
     * ruta y sobrevive a la navegación, así que una rama que no asigna hereda lo
     * que dejó la visita anterior — editar un alquiler y después un servicio
     * marcaba Rentals.
     */
    setNavActiveLink(route, res.offering.kind === 'product'
      ? 'dive-centers-id-offerings-rentals'
      : 'dive-centers-id-offerings-services')
  }
  catch (e) {
    loadError.value = e instanceof ApiError ? e.message : 'Could not load offering.'
  }
  finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="mx-auto" style="max-width: 1100px;">
    <div
      v-if="loading"
      class="d-flex justify-center py-16"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </div>

    <VAlert
      v-else-if="loadError"
      type="error"
      variant="tonal"
      density="compact"
    >
      {{ loadError }}
    </VAlert>

    <OfferingWizard
      v-else-if="offering"
      :dc-id="id"
      :offering="offering"
      :start-at-documents="startAtDocuments"
    />
  </div>
</template>
